/**
 * ALERTS — an admin/manager's notice to the roles under them.
 *
 *   SILENT   → lands in the Alert box (badge + jiggle on the header icon) and
 *              rings the chat-style corner toast when it arrives live.
 *   ANNOUNCE → the same row PLUS a popup on every recipient's screen, live
 *              over the socket and on login while it is still unread.
 *
 * Wire shapes mirror the server's `core/alerts/alert.service.ts`.
 */
export type AlertMode = "SILENT" | "ANNOUNCE";

export interface AlertSender {
  id: number;
  name: string | null;
  avatar?: string | null;
}

export type AssignmentRequestStatus = "PENDING" | "DONE" | "CANCELLED";

export interface AssignmentRequestPerson {
  id: number;
  name: string | null;
  roleName?: string | null;
}

/**
 * An assignment request riding an alert (`core/assignment-requests`): someone
 * who cannot assign asked a person or a role to assign a lead / deal. The
 * popup keeps coming back while it is PENDING; Done, an assignment on the
 * lead, or the requester's cancel closes it. Mirrors the server's
 * `assignment-request.helpers.ts`.
 */
export interface AssignmentRequestSummary {
  id: number;
  status: AssignmentRequestStatus;
  /** "done" | "assigned" | "cancelled" once closed. */
  resolution: string | null;
  leadId: number;
  childId: number | null;
  leadName: string;
  leadPhone: string | null;
  dealName: string | null;
  note: string | null;
  requestedBy: AssignmentRequestPerson;
  suggested: AssignmentRequestPerson | null;
  target: { kind: "user" | "role"; id: number | null; name: string };
  recipientCount: number;
  /** The alert carrying it — null in the live push, which is the alert itself. */
  alertId: number | null;
  resolvedBy: AssignmentRequestPerson | null;
  resolvedAt: string | null;
  createdAt: string;
}

/** One row of the caller's Alert box (`GET /core/alerts`). `id` is the alert. */
export interface AlertInboxItem {
  id: number;
  recipientId?: number;
  title: string;
  message: string;
  mode: AlertMode;
  createdAt: string;
  readAt: string | null;
  createdBy: AlertSender;
  /** Set when the alert carries an assignment request. */
  request?: AssignmentRequestSummary | null;
}

/** One alert the caller sent (`GET /core/alerts/sent`). */
export interface AlertSentItem {
  id: number;
  title: string;
  message: string;
  mode: AlertMode;
  createdAt: string;
  recipientCount: number;
  readCount: number;
  audienceSummary: string;
  request?: AssignmentRequestSummary | null;
}

export interface AlertInboxPage {
  data: AlertInboxItem[];
  nextCursor: string | null;
  hasMore: boolean;
  unreadCount: number;
  /** Unread ANNOUNCE alerts — first page only; the popup host drains them. */
  pendingAnnouncements?: AlertInboxItem[];
}

export interface AlertSentPage {
  data: AlertSentItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface AlertComposeRole {
  id: number;
  name: string;
  hierarchy: number;
  userCount: number;
}
export interface AlertComposeBranch {
  id: number;
  name: string;
}
export interface AlertComposeField {
  id: number;
  name: string;
  options: Array<{ id: number; value: string }>;
}

/** `GET /core/alerts/compose-options` — what this sender may pick from. */
export interface AlertComposeOptions {
  canCreate: boolean;
  /** Tenant admins reach everyone; anyone else reaches the roles below them. */
  scope: "everyone" | "below";
  roles: AlertComposeRole[];
  branches: AlertComposeBranch[];
  fields: AlertComposeField[];
}

export interface AlertAudience {
  roleIds: number[];
  branchIds: number[];
  cfItemIds: number[];
}

export interface AlertAudiencePreview {
  count: number;
  sample: string[];
  summary: string;
}

/** Socket `alert:new`. */
export interface AlertNewPush {
  id?: number;
  alertId?: number;
  title: string;
  message: string;
  mode: AlertMode;
  createdAt: string;
  createdBy: AlertSender;
  request?: AssignmentRequestSummary | null;
  timestamp?: string;
}

/** Socket `alert:updated`. `request_resolved` = the request the alert carries closed. */
export interface AlertUpdatedPush {
  action: "read" | "read_all" | "removed" | "request_resolved";
  alertId: number | null;
  unreadCount: number | null;
  request?: AssignmentRequestSummary | null;
  timestamp?: string;
}

/** "Just now", "5m ago", "3h ago", "2d ago", "1w ago". */
export function alertTimeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  if (!Number.isFinite(diff)) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}
